import { useEffect, useState } from "react";
import type {
  DraftLineInput,
  DraftLineDefinition,
  SubscriptionView,
  DraftQuoteView,
} from "@vereinfach/types";
import { api, errorMessage } from "../lib/api";
import { formatCHF } from "../lib/format";

const iso = (value: number) =>
  Number.isFinite(value) ? new Date(value * 1000).toISOString().slice(0, 10) : "";
const unix = (value: string) => Date.parse(value + "T00:00:00Z") / 1000;
export function InvoiceLinesEditor({
  tenantId,
  invoiceId,
  memberId,
  refreshToken,
  value,
  onChange,
  onQuote,
}: {
  tenantId: string;
  invoiceId: string;
  memberId: string | null;
  refreshToken: number;
  value: DraftLineInput[];
  onChange: (v: DraftLineInput[]) => void;
  onQuote: (q: DraftQuoteView | null) => void;
}) {
  const [subscriptions, setSubscriptions] = useState<SubscriptionView[]>([]);
  const [quote, setQuote] = useState<DraftQuoteView | null>(null);
  const [error, setError] = useState("");
  const key = JSON.stringify(value);
  useEffect(() => {
    if (!memberId) return;
    let active = true;
    api<{ subscriptions: SubscriptionView[] }>(
      `/tenants/${tenantId}/members/${memberId}/subscriptions`,
    )
      .then((r) => {
        if (active) setSubscriptions(r.subscriptions);
      })
      .catch((e) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [tenantId, memberId]);
  useEffect(() => {
    let active = true;
    setQuote(null);
    onQuote(null);
    const timer = setTimeout(() => {
      api<DraftQuoteView>(`/tenants/${tenantId}/invoices/${invoiceId}/quote`, {
        method: "POST",
        body: JSON.stringify({ lines: JSON.parse(key) }),
      })
        .then((q) => {
          if (active) {
            setQuote(q);
            onQuote(q);
            setError("");
          }
        })
        .catch((e) => {
          if (active) {
            setError(errorMessage(e));
            onQuote(null);
          }
        });
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [key, tenantId, invoiceId, refreshToken, onQuote]);
  const definitions = value.map(
    (l) =>
      l.definition ?? {
        kind: "charge" as const,
        description: l.description,
        amountRappen: l.amount_rappen,
      },
  );
  function change(next: DraftLineDefinition[]) {
    onQuote(null);
    onChange(
      next.map((definition) => ({
        description: definition.description,
        amount_rappen: definition.kind === "charge" ? definition.amountRappen : 0,
        definition,
      })),
    );
  }
  function update(i: number, line: DraftLineDefinition) {
    change(definitions.map((v, j) => (i === j ? line : v)));
  }
  function create(kind: DraftLineDefinition["kind"], description = ""): DraftLineDefinition {
    if (kind === "text") return { kind, description };
    if (kind === "discount")
      return { kind, description, discount: { kind: "percent", basisPoints: 1000 } };
    if (kind === "subscription") {
      const sub = subscriptions[0];
      return {
        kind,
        description: description || sub?.package_name || "",
        subscriptionId: sub?.id ?? "",
        period: { start: sub?.current_start ?? 0, end: sub?.current_end ?? 86400 },
      };
    }
    return { kind, description, amountRappen: 0 };
  }
  return (
    <div className="space-y-3">
      {definitions.map((line, i) => (
        <div key={i} className="space-y-2 rounded-lg border p-3">
          <div className="flex gap-2">
            <select
              aria-label="Positionsart"
              className="input w-40"
              value={line.kind}
              onChange={(e) =>
                update(i, create(e.target.value as DraftLineDefinition["kind"], line.description))
              }
            >
              <option value="charge">Betrag</option>
              <option value="text">Text</option>
              <option value="subscription">Abo</option>
              <option value="discount">Rabatt</option>
            </select>
            <input
              aria-label="Beschreibung"
              className="input flex-1"
              value={line.description}
              onChange={(e) => update(i, { ...line, description: e.target.value })}
            />
            <button
              aria-label="Position entfernen"
              disabled={definitions.length === 1}
              onClick={() => change(definitions.filter((_, j) => i !== j))}
            >
              ✕
            </button>
          </div>
          {line.kind === "charge" && (
            <label className="block text-sm">
              Betrag CHF
              <input
                type="number"
                step="0.01"
                className="input"
                value={line.amountRappen / 100}
                onChange={(e) =>
                  update(i, { ...line, amountRappen: Math.round(Number(e.target.value) * 100) })
                }
              />
            </label>
          )}
          {line.kind === "subscription" && (
            <div className="grid grid-cols-2 gap-2">
              <select
                aria-label="Abo"
                className="input col-span-2"
                value={line.subscriptionId}
                onChange={(e) => {
                  const sub = subscriptions.find((s) => s.id === e.target.value);
                  if (sub)
                    update(i, {
                      ...line,
                      subscriptionId: sub.id,
                      description: sub.package_name,
                      period: { start: sub.current_start, end: sub.current_end },
                    });
                }}
              >
                <option value="">Abo wählen</option>
                {subscriptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.package_name}
                  </option>
                ))}
              </select>
              <label>
                Von
                <input
                  type="date"
                  className="input"
                  value={iso(line.period.start)}
                  onChange={(e) =>
                    update(i, { ...line, period: { ...line.period, start: unix(e.target.value) } })
                  }
                />
              </label>
              <label>
                Bis einschliesslich
                <input
                  type="date"
                  className="input"
                  value={iso(line.period.end - 86400)}
                  onChange={(e) =>
                    update(i, {
                      ...line,
                      period: { ...line.period, end: unix(e.target.value) + 86400 },
                    })
                  }
                />
              </label>
              <label className="col-span-2">
                Betrag überschreiben (CHF, optional)
                <input
                  type="number"
                  step="0.01"
                  className="input"
                  value={line.amountOverride === undefined ? "" : line.amountOverride / 100}
                  onChange={(e) =>
                    update(i, {
                      ...line,
                      amountOverride:
                        e.target.value === ""
                          ? undefined
                          : Math.round(Number(e.target.value) * 100),
                    })
                  }
                />
              </label>
              <p className="col-span-2 text-xs text-slate-500">
                Ohne Überschreibung wird der Betrag anteilig berechnet. Die Abo-Laufzeit bleibt
                unverändert.
              </p>
            </div>
          )}
          {line.kind !== "text" && (
            <div className="flex items-center gap-2">
              <select
                aria-label="Rabattart"
                className="input w-48"
                value={"discount" in line && line.discount ? line.discount.kind : "none"}
                onChange={(e) => {
                  const discount =
                    e.target.value === "none"
                      ? undefined
                      : e.target.value === "fixed"
                        ? { kind: "fixed" as const, amountRappen: 0 }
                        : { kind: "percent" as const, basisPoints: 0 };
                  if (line.kind === "discount" && !discount) return;
                  update(i, { ...line, discount } as DraftLineDefinition);
                }}
              >
                {line.kind !== "discount" && <option value="none">Kein Positionsrabatt</option>}
                <option value="percent">Rabatt %</option>
                <option value="fixed">Rabatt CHF</option>
              </select>
              {"discount" in line && line.discount && (
                <input
                  aria-label="Rabatt"
                  type="number"
                  step="0.01"
                  min="0"
                  className="input w-32"
                  value={
                    (line.discount.kind === "fixed"
                      ? line.discount.amountRappen
                      : line.discount.basisPoints) / 100
                  }
                  onChange={(e) => {
                    const n = Math.round(Number(e.target.value) * 100);
                    update(i, {
                      ...line,
                      discount:
                        line.discount!.kind === "fixed"
                          ? { kind: "fixed", amountRappen: n }
                          : { kind: "percent", basisPoints: n },
                    } as DraftLineDefinition);
                  }}
                />
              )}
            </div>
          )}
          {line.kind === "discount" && (
            <p className="text-xs text-slate-500">
              Gilt für die Zwischensumme aller vorherigen Positionen.
            </p>
          )}
          {quote?.lines[i] && line.kind !== "text" && (
            <p className="text-right text-sm">{formatCHF(quote.lines[i]!.amountRappen)}</p>
          )}
        </div>
      ))}
      <button className="btn-sm" onClick={() => change([...definitions, create("charge")])}>
        + Position hinzufügen
      </button>
      {quote && (
        <div className="text-right">
          <p>MwSt {formatCHF(quote.vatRappen)}</p>
          <strong>Total {formatCHF(quote.totalRappen)}</strong>
        </div>
      )}
      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
